// @ts-nocheck
import { useRef, useState } from 'react';
import Papa from 'papaparse';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Upload } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { HotelTeamPicker, PickerTeam } from './HotelTeamPicker';
import { invalidateRoomBlockQueries } from './hooks/useRoomBlocks';
import { STAYBOOK_HEADERS, normalizeHeaders, parseStaybookRow, uniqueStaybookCodes, matchByName, StaybookRow } from './roomBlockUtils';

interface PreviewRow extends StaybookRow {
  blockCode: string;
  registrationId?: string;
  hotelPropertyId?: string;
}

export function StaybookUpload({ eventId, teams, hotels }: {
  eventId: string;
  teams: PickerTeam[];
  hotels: { id: string; hotel_name: string }[];
}) {
  const { user } = useAuth();
  const input = useRef<HTMLInputElement>(null);
  const qc = useQueryClient();
  const [rows, setRows] = useState<PreviewRow[]>([]);
  const [links, setLinks] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [fileName, setFileName] = useState('');
  const [result, setResult] = useState<{ imported: number; updated: number } | null>(null);

  function handleFile(file?: File) {
    if (!file || busy) return;
    setRows([]); setLinks({}); setResult(null); setFileName(file.name);
    Papa.parse(file, { header: true, skipEmptyLines: 'greedy',
      complete: results => {
        const headers = normalizeHeaders(results.meta.fields);
        const missing = STAYBOOK_HEADERS.filter(h => !headers.includes(h));
        if (missing.length) { toast.error(`Missing required columns: ${missing.join(', ')}`); return; }
        const parsed = (results.data as Record<string, string>[])
          .map(parseStaybookRow).filter(Boolean) as StaybookRow[];
        if (!parsed.length) { toast.error('No team rows found in this report.'); return; }
        const codes = uniqueStaybookCodes(parsed);
        setRows(parsed.map((row, i) => ({
          ...row,
          blockCode: codes[i],
          registrationId: matchByName(teams, 'team_name', row.teamName)?.registration_id,
          hotelPropertyId: matchByName(hotels, 'hotel_name', row.propertyName)?.id,
        })));
      },
      error: error => toast.error(error.message || 'Unable to read CSV'),
    });
  }

  async function handleImport() {
    if (!rows.length || busy || !user) return;
    setBusy(true);
    let imported = 0, updated = 0;
    try {
      // Pre-check every existing block_code for this event once, so counts reflect real rows.
      const existingByCode = new Map<string, string>();
      for (let from = 0; ; from += 1000) {
        const { data: page, error: listError } = await (supabase as any).from('hotel_room_blocks')
          .select('id, block_code').eq('event_id', eventId).range(from, from + 999);
        if (listError) throw listError;
        for (const r of page || []) if (r.block_code) existingByCode.set(r.block_code, r.id);
        if (!page || page.length < 1000) break;
      }
      for (const row of rows) {
        const registrationId = links[row.blockCode] || row.registrationId || null;
        const data = {
          event_id: eventId,
          block_code: row.blockCode,
          group_name: row.teamName,
          hotel_name: row.propertyName,
          hotel_property_id: row.hotelPropertyId || null,
          registration_id: registrationId,
          match_method: registrationId ? (links[row.blockCode] ? 'manual' : 'staybook_name') : null,
          rooms_held: 0,
          rooms_reserved: 0,
          room_nights_available: 0,
          reservations_booked: row.reservationsBooked,
          room_nights_booked: row.roomNightsBooked,
          room_nights_on_hold: row.roomNightsOnHold,
          age_group: row.ageGroup || null,
          notes: null,
          source: 'staybook',
        };
        const existingId = existingByCode.get(row.blockCode);
        if (existingId) {
          const { error } = await (supabase as any).from('hotel_room_blocks').update(data).eq('id', existingId);
          if (error) throw error;
          updated++;
        } else {
          const { data: inserted, error } = await (supabase as any).from('hotel_room_blocks')
            .insert({ ...data, created_by: user.id }).select('id').single();
          if (error) throw error;
          if (inserted?.id) existingByCode.set(row.blockCode, inserted.id);
          imported++;
        }
      }
      setResult({ imported, updated });
      toast.success(`Imported ${imported} new, updated ${updated} pickup rows.`);
      await invalidateRoomBlockQueries(qc);
    } catch (error) {
      toast.error(error.message || 'Unable to import report');
    } finally { setBusy(false); }
  }

  return <div className="space-y-4">
    <div className="border-2 border-dashed rounded-md p-6 sm:p-10 text-center space-y-3"
      onDragOver={e => e.preventDefault()} onDrop={e => { e.preventDefault(); handleFile(e.dataTransfer.files[0]); }}>
      <Upload className="mx-auto h-8 w-8 text-muted-foreground" />
      <p className="text-sm break-all">{fileName || 'Drop a Staybook Client Reporting Dashboard CSV here'}</p>
      <Button variant="outline" className="min-h-11" disabled={busy} onClick={() => input.current?.click()}>{busy ? 'Importing…' : 'Choose CSV'}</Button>
      <input ref={input} type="file" accept=".csv,text/csv" className="hidden" onChange={e => { handleFile(e.target.files?.[0]); e.target.value = ''; }} />
    </div>
    <p className="text-sm text-muted-foreground">Re-uploading the same report updates existing rows by team, hotel and age group. Staybook numbers are stored as reservations and room nights, separate from room-block counts.</p>
    {rows.length > 0 && <>
      <div className="overflow-x-auto border rounded-md"><Table><TableHeader><TableRow>
        {['Team', 'Age group', 'Hotel', 'Reservations', 'Nights booked', 'Nights on hold', 'Status', 'Link team'].map(label => <TableHead key={label}>{label}</TableHead>)}
      </TableRow></TableHeader><TableBody>
        {rows.map(row => {
          const registrationId = links[row.blockCode] || row.registrationId;
          const teamName = teams.find(t => t.registration_id === registrationId)?.team_name;
          return <TableRow key={row.blockCode}>
            <TableCell>{row.teamName}</TableCell>
            <TableCell>{row.ageGroup || '—'}</TableCell>
            <TableCell>{row.propertyName}{!row.hotelPropertyId && <p className="text-xs text-muted-foreground">Hotel not on event</p>}</TableCell>
            <TableCell>{row.reservationsBooked}</TableCell>
            <TableCell>{row.roomNightsBooked}</TableCell>
            <TableCell>{row.roomNightsOnHold}</TableCell>
            <TableCell><Badge variant="outline" className={registrationId ? 'bg-success/10 text-success border-success/30' : 'bg-warning/10 text-warning border-warning/30'}>
              {registrationId ? `Linked${teamName ? ` · ${teamName}` : ''}` : 'Unlinked'}</Badge></TableCell>
            <TableCell>{!row.registrationId && <HotelTeamPicker teams={teams} value={links[row.blockCode]} disabled={busy}
              onChange={id => setLinks(prev => ({ ...prev, [row.blockCode]: id }))} />}</TableCell>
          </TableRow>;
        })}
      </TableBody></Table></div>
      <div className="flex flex-wrap items-center gap-3">
        <Button className="min-h-11" onClick={handleImport} disabled={busy}>{busy ? 'Importing…' : `Import ${rows.length} rows`}</Button>
        {result && <p className="text-sm text-muted-foreground">{result.imported} imported, {result.updated} updated</p>}
      </div>
    </>}
  </div>;
}
